import React, { useState, useEffect } from 'react';
import { Box, Button, Flex, FormControl, FormLabel, Input, Table, Tbody, Td, Th, Thead, Tr, VStack, Textarea, Select, List, ListItem, Text, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import styles from '../Tabel.module.css';
import { ACCOUNTS_URL } from '../../../../Globalapi/Globalapi';

const MEEdit = () => {
    const { voucherNo: initialVoucherNo } = useParams();  // Get voucherNo from URL params
    const [rows, setRows] = useState([]);
    const [voucherNo, setVoucherNo] = useState(initialVoucherNo || '');
    const [date, setDate] = useState('');
    const [narration, setNarration] = useState('');
    const [fromSuggestions, setFromSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [cmpId] = useState(sessionStorage.getItem('cmp_id'));
    const toast = useToast();
    const navigate = useNavigate();  // If you need to navigate programmatically after saving
    const [companyData, setCompanyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userId, setUserId] = useState(null);
    const [error, setError] = useState(null);
    // Fetch Voucher Data on component load
    useEffect(() => {
        const fetchVoucherData = async () => {
            if (!voucherNo) return;

            try {
                const response = await axios.post(`${ACCOUNTS_URL}/multi_voucher/load_single_multi_voucher.php`, {
                    comp_id: cmpId,
                    voucher_no: voucherNo
                });
                console.log('====================================');
                console.log(response);
                console.log('====================================');

                if (response.data.success) {
                    const voucher = response.data.data[0]; // Assuming only one voucher is returned

                    setDate(voucher.date);
                    setNarration(voucher.narration);

                    const entries = voucher.entries.map(entry => ({
                        id: entry.in_no,
                        byTo: entry.debit > 0 ? "By" : "To",
                        particulars: entry.ledger_name,
                        debit: entry.debit,
                        credit: entry.credit,
                        debitEnabled: entry.debit !== '0.00',
                        creditEnabled: entry.credit !== '0.00',
                        Ldg_id: entry.l_id
                    }));

                    setRows(entries);
                } else {
                    toast({
                        title: "Error",
                        description: response.data.message,
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error('Error loading voucher data:', error);
                toast({
                    title: "Error",
                    description: "There was an error loading the voucher data.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
            }
        };

        fetchVoucherData();
    }, [voucherNo, cmpId, toast]);

    // Function to handle "Particulars" input changes and fetching suggestions
    const handleParticularsChange = (index, value) => {
        const updatedRows = [...rows];
        updatedRows[index].particulars = value;
        setRows(updatedRows);

        if (value.length >= 1) {
            fetchFromSuggestions(value);
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
        }
    };

    // Fetch suggestions for "Particulars" based on input
    const fetchFromSuggestions = async (text) => {
        try {
            const response = await axios.post(`${ACCOUNTS_URL}/common/ledger_name.php`, {
                cmp_id: cmpId,
                fectxt: text
            });

            if (response.data.status === 'success') {
                setFromSuggestions(response.data.data);
            } else {
                setFromSuggestions([]);
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setFromSuggestions([]);
        }
    };

    // Handle the "Particulars" selection from suggestions
    const selectSuggestion = (index, suggestion) => {
        const updatedRows = [...rows];
        updatedRows[index].particulars = suggestion.L_name;
        updatedRows[index].Ldg_id = suggestion.Ldg_id; // Store the ledger ID for the selected row
        setRows(updatedRows);
        setShowDropdown(false);
    };

    // Handle the "By/To" selection change
    const handleByToChange = (index, value) => {
        const updatedRows = rows.map((row, i) => {
            if (i === index) {
                row.byTo = value;
                row.debitEnabled = value === 'By';
                row.creditEnabled = value === 'To';

                if (value === 'By') {
                    row.credit = ''; // Clear credit when changing to "By"
                } else if (value === 'To') {
                    row.debit = ''; // Clear debit when changing to "To"
                }
            }
            return row;
        });
        setRows(updatedRows);
    };

    // Delete a row from the table
    const deleteRow = (index) => {
        const updatedRows = rows.filter((_, i) => i !== index);
        setRows(updatedRows);
    };

    // Handle changes to Debit amount
    const handleDebitChange = (index, value) => {
        const updatedRows = [...rows];
        updatedRows[index].debit = value;
        setRows(updatedRows);
    };

    // Handle changes to Credit amount
    const handleCreditChange = (index, value) => {
        const updatedRows = [...rows];
        updatedRows[index].credit = value;
        setRows(updatedRows);
    };

    // Handle Save functionality with validation
    const handleSave = async () => {
        // Debugging: Log rows data
        console.log("Rows data:", rows);

        // Calculate total debit and credit
        const totalDebit = rows
            .filter(row => row.byTo === 'To') // Ensure we're filtering correctly for the 'To' rows
            .reduce((acc, row) => {
                // Remove commas before parsing and parse the value as a float
                const debitValue = parseFloat(row.debit.replace(/,/g, ''));
                console.log('Row debit value:', row.debit, 'Parsed debit value:', debitValue);
                return acc + (isNaN(debitValue) ? 0 : debitValue); // Only add if it's a valid number
            }, 0);

        console.log('====================================');
        console.log("Total Debit:", totalDebit);
        console.log('====================================');

        // Calculate total credit
        const totalCredit = rows
            .filter(row => row.byTo === 'To') // Ensure we're filtering correctly for the 'To' rows
            .reduce((acc, row) => {
                // Remove commas before parsing and parse the value as a float
                const creditValue = parseFloat(row.credit.replace(/,/g, ''));
                console.log('Row credit value:', row.credit, 'Parsed credit value:', creditValue);
                return acc + (isNaN(creditValue) ? 0 : creditValue); // Only add if it's a valid number
            }, 0);

        console.log('====================================');
        console.log("Total Credit:", totalCredit);
        console.log('====================================');

        // Validate if debit and credit totals match
        if (totalDebit !== totalCredit) {
            const difference = Math.abs(totalDebit - totalCredit);
            toast({
                title: "Voucher Validation Failed",
                description: `The debit and credit totals don't match. You need to balance the transaction by ${difference}.`,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        try {
            // Mapping the data correctly
            const data = rows.map(row => ({
                in_no: row.id,  // Include in_no for the row to be updated
                l_id: row.Ldg_id, // Use Ldg_id instead of particulars
                dr: parseFloat(row.debit.replace(/,/g, '')) || 0,  // Ensure it's parsed as a number
                cr: parseFloat(row.credit.replace(/,/g, '')) || 0,  // Ensure it's parsed as a number
            }));

            const p = {
                user_id: sessionStorage.getItem('user_id'),  // Replace with actual user ID
                comp_id: cmpId,
                narration,
                date,
                voucher_no: voucherNo,
                data
            }
            console.log('====================================');
            console.log("sss", p);
            console.log('====================================');

            const response = await axios.post(`${ACCOUNTS_URL}/multi_voucher/update_multi_voucher.php`, {
                user_id: sessionStorage.getItem('user_id'),  // Replace with actual user ID
                comp_id: cmpId,
                narration,
                date,
                voucher_no: voucherNo,
                data
            });

            if (response.data.success) {
                toast({
                    title: "Voucher Saved",
                    description: "Voucher has been saved successfully.",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
                navigate('/voucher/multientry');  // Redirect to voucher list or another page
            } else {
                toast({
                    title: "Save Failed",
                    description: `Failed to save voucher: ${response.data.message}`,
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error saving voucher:', error);
            toast({
                title: "Error",
                description: "There was an error saving the voucher.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        const storedUserId = sessionStorage.getItem('user_id');
        if (storedUserId) {
            setUserId(storedUserId);
        } else {
            setError('User not logged in or user_id not found in sessionStorage');
        }
    }, []);

    useEffect(() => {
        if (userId) {
            const fetchCompanyDetails = async () => {
                try {
                    const response = await fetch(`${ACCOUNTS_URL}/settings/company_details.php`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ user_id: userId }),
                    });

                    const data = await response.json();
                    console.log('====================================');
                    console.log(data);
                    console.log('====================================');
                    if (data.status === 200) {
                        setCompanyData(data.data);
                    } else {
                        setError('No company found for this user.');
                    }
                } catch (err) {
                    setError('Error fetching company details.');
                } finally {
                    setLoading(false);
                }
            };

            fetchCompanyDetails();
        }
    }, [userId]); // Run when userId is set


    const printVoucher = () => {
        // Detect if the user is on a mobile device
        const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    
        // Filter rows for credit ("By") and debit ("To")
        const byRows = rows.filter(row => row.credit !== '0.00' && row.credit !== undefined && row.credit !== null); // "By" is the credit side
        const toRows = rows.filter(row => row.debit !== '0.00' && row.debit !== undefined && row.debit !== null); // "To" is the debit side
    
        // Calculate the total of debit values for the "To" rows
        const totalDebit = toRows.reduce((total, row) => total + parseFloat(row.debit.replace(/,/g, '')), 0);
    
        console.log('By Rows:', byRows);
        console.log('To Rows:', toRows);
        console.log('Total Debit:', totalDebit);
    
        const printContent = `
            <html>
                <head>
                    <title>Voucher No: ${voucherNo}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        h2, h4 { margin-bottom: 5px; }
                        p { margin-top: 5px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                        th, td { padding: 10px; text-align: left; border: 1px solid black; }
                        th { background-color: #f9f9f9; }
                        .header-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
                        .total-row { font-weight: bold; }
                        .amount-text { margin-top: 10px; font-style: italic; }
    
                        .header, .footer { text-align: center; font-weight: bold; margin-bottom: 10px; }
                        .header h2 { margin: 0; font-size: 24px; }
                        .header p { margin: 0; font-size: 14px; }
                        .recipient-info { text-align: left; margin: 20px 0; font-size: 16px; }
                        .recipient-info p { margin: 0; }
                        .details-table, .amount-table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 16px; }
                        .details-table th, .details-table td, .amount-table th, .amount-table td { 
                          border: 1px solid #ddd; padding: 8px; text-align: left; 
                        }
                        .amount-table th { background-color: #f2f2f2; }
                        .footer-note { margin-top: 20px; text-align: center; font-size: 14px; }
    
                        /* Print Styles */
                        @media print {
                          body {
                            margin: 0;
                            width: 100%;
                            -webkit-print-color-adjust: exact;
                          }
                          .header h2 { font-size: 20px; }
                          .header p { font-size: 12px; }
                          .amount-table, .details-table {
                            max-width: 700px;
                            margin: auto;
                          }
                          .amount-table th, .amount-table td {
                            padding: 6px;
                          }
                          .footer-note {
                            font-size: 12px;
                          }
                          /* Page break for long content */
                          .page-break { 
                            page-break-before: always; 
                          }
                        }
                    </style>
                </head>
                <body>
                   <div class="header">
    ${companyData.Company_Name && companyData.Company_Name.startsWith("No") === false 
        ? `<h2>${companyData.Company_Name}</h2>` 
        : ""}
    ${companyData.address && companyData.address.startsWith("No") === false 
        ? `<p>${companyData.address}</p>` 
        : ""}
    ${companyData.Tin && companyData.Tin.startsWith("No") === false 
        ? `<p>GSTIN/UIN: ${companyData.Tin}</p>` 
        : ""}
    ${companyData.Email_Id && companyData.Email_Id.startsWith("No") === false 
        ? `<p>Email: ${companyData.Email_Id}</p>` 
        : ""}
</div>
                    <div class="header-info">
                        <div>
                            <h4>TO: ${byRows.map(row => row.particulars).join(', ')}</h4>
                        </div>
                        <div>
                            <p>No: ${voucherNo}</p>
                            <p>Date: ${date}</p>
                        </div>
                    </div>
        
                    <table>
                        <thead>
                            <tr>
                                <th>Sl No.</th>
                                <th>Description</th>
                                <th style="text-align: right;">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${toRows.map((row, index) => `
                                <tr>
                                    <td>${index + 1}</td>
                                    <td>${row.particulars}</td>
                                    <td style="text-align: right;">${row.debit}</td>
                                </tr>
                            `).join('')}
                            <!-- Total Row -->
                            <tr class="total-row">
                                <td colspan="2"><strong>Total</strong></td>
                                <td style="text-align: right;"><strong>${totalDebit.toFixed(2)}</strong></td>
                            </tr>
                             
                        </tbody>
                    </table>
                </body>
            </html>
        `;
    
        if (isMobile) {
            // For mobile, open a new window and print the content
            const printWindow = window.open('', '_blank');
            printWindow.document.open();
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.print();
        } else {
            // For desktop, use iframe to print content
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.top = '-9999px'; // Hide the iframe
            document.body.appendChild(iframe);
    
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(printContent);
            iframeDoc.close();
    
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
    
            document.body.removeChild(iframe);
        }
    };
    

    return (
        <Box borderRadius='20px' background='white' mx="auto">
            <Flex justifyContent="space-between" mb="4" p="20px">
                <FormControl maxW="20%">
                    <FormLabel>Voucher No</FormLabel>
                    <Input value={voucherNo} readOnly placeholder="Voucher No" />
                </FormControl>
                <FormControl maxW="20%">
                    <FormLabel>Date</FormLabel>
                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </FormControl>
            </Flex>

            <Box minHeight='400px' style={{ overflowX: 'auto' }} w={{ base: '367px', md: '100%' }} className={styles.Table}>
                <Table mb="4">
                    <Thead>
                        <Tr>
                            <Th textAlign='left' textTransform='initial'>By/To</Th>
                            <Th textAlign='left' textTransform='initial'>Particulars</Th>
                            <Th textAlign='right' textTransform='initial'>Debit</Th>
                            <Th textAlign='right' textTransform='initial'>Credit</Th>
                            <Th textAlign='center'>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {rows.map((row, index) => (
                            <Tr key={row.id}>
                                <Td>
                                    <Select
                                        maxW="80px"
                                        value={row.byTo}
                                        onChange={(e) => handleByToChange(index, e.target.value)}
                                    >
                                        <option value="By">By</option>
                                        <option value="To">To</option>
                                    </Select>
                                </Td>
                                <Td position="relative">
                                    <Input
                                        value={row.particulars}
                                        onChange={(e) => handleParticularsChange(index, e.target.value)}
                                        placeholder="Enter Particulars"
                                    />
                                    {showDropdown && fromSuggestions.length > 0 && (
                                        <Box
                                            border="1px solid #ccc"
                                            borderRadius="md"
                                            mt={1}
                                            maxH="150px"
                                            overflowY="auto"
                                            bg="white"
                                            zIndex={1}
                                            position="absolute"
                                            w="100%"
                                        >
                                            <List spacing={0}>
                                                {fromSuggestions.map((suggestion) => (
                                                    <ListItem
                                                        key={suggestion.Ldg_id}
                                                        p={2}
                                                        _hover={{ backgroundColor: 'gray.100', cursor: 'pointer' }}
                                                        onClick={() => selectSuggestion(index, suggestion)}
                                                    >
                                                        {suggestion.L_name}
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                    {showDropdown && fromSuggestions.length === 0 && (
                                        <Text p={2}>No suggestions found</Text>
                                    )}
                                </Td>
                                <Td isNumeric>
                                    <Input
                                        isDisabled={!row.debitEnabled}
                                        value={row.debit}
                                        onChange={(e) => handleDebitChange(index, e.target.value)}
                                        textAlign='right'
                                    />
                                </Td>
                                <Td isNumeric>
                                    <Input
                                        isDisabled={!row.creditEnabled}
                                        value={row.credit}
                                        onChange={(e) => handleCreditChange(index, e.target.value)}
                                        textAlign='right'
                                    />
                                </Td>
                                <Td textAlign="center">
                                    <Button colorScheme="red" size="sm" onClick={() => deleteRow(index)}>Delete</Button>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            <Textarea
                mt={4}
                placeholder="Narration"
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
            />

            <Button colorScheme="blue" mt={4} onClick={handleSave}>Save</Button>
            <Button bg='#4CAF50' color='white' mt={4} ml={2} onClick={() => {
                handleSave();
                printVoucher();
            }}>Print</Button>

        </Box>
    );
};

export default MEEdit;
